import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import staticMugFrame from '../assets/frame_00000.png';
import { MugPlayRequest } from '../hooks/useMugAnimationController';
import { useShellLayout } from '../hooks/useShellLayout';
import { getMugOverlayStyle } from './mugOverlay';

interface StaticMugProps {
    cheersPlaying: boolean;
    mugRequest: MugPlayRequest;
}

export interface StaticMugHandle {
    handleMugFrameNearEnd: (half: 'top' | 'bottom', direction: 'forward' | 'reverse') => void;
    /** Called by CheersAnimation 1 frame before ending */
    handleCheersNearEnd: () => void;
}

/**
 * StaticMug component - displays the idle mug frame (frame_00000.png).
 * 
 * Visibility Logic:
 * - Both halves are visible during Day phase (when nothing is playing).
 * - Both halves hide when Cheers starts, reappear when Cheers ends.
 * - A specific half hides when TakePutMug starts 'reverse' on that half.
 * - A specific half shows when TakePutMug finishes 'forward' on that half (via callback).
 */
const StaticMug = forwardRef<StaticMugHandle, StaticMugProps>(({ cheersPlaying, mugRequest }, ref) => {
    const { isMobileShell } = useShellLayout();
    // Visibility state for each half (true = visible)
    const [topVisible, setTopVisible] = useState(true);
    const [bottomVisible, setBottomVisible] = useState(true);

    // Image preload state
    const [imageLoaded, setImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement | null>(null);

    // Track previous Cheers state
    const [prevCheersPlaying, setPrevCheersPlaying] = useState(false);

    // Track last processed request keys to detect new requests
    const [lastTopKey, setLastTopKey] = useState<number | null>(null);
    const [lastBottomKey, setLastBottomKey] = useState<number | null>(null);

    // --- Preload Image on Mount ---
    useEffect(() => {
        const img = new Image();
        img.src = staticMugFrame;
        img.onload = () => {
            imageRef.current = img;
            setImageLoaded(true);
        };
    }, []);

    // --- Cheers Logic ---
    useEffect(() => {
        // Cheers just started: hide both
        if (cheersPlaying && !prevCheersPlaying) {
            setTopVisible(false);
            setBottomVisible(false);
        }
        // Note: Cheers end is now handled via handleCheersNearEnd callback (shows 1 frame before end)
        setPrevCheersPlaying(cheersPlaying);
    }, [cheersPlaying, prevCheersPlaying]);

    // --- TakePutMug Logic (TOP half) ---
    useEffect(() => {
        if (!mugRequest.top || mugRequest.top.requestKey === lastTopKey) return;

        const { direction, requestKey } = mugRequest.top;
        setLastTopKey(requestKey);

        if (direction === 'reverse') {
            // Reverse started: hide top immediately
            setTopVisible(false);
        }
        // Forward: DO NOT show here - wait for handleMugFrameNearEnd callback
    }, [mugRequest.top, lastTopKey]);

    // --- TakePutMug Logic (BOTTOM half) ---
    useEffect(() => {
        if (!mugRequest.bottom || mugRequest.bottom.requestKey === lastBottomKey) return;

        const { direction, requestKey } = mugRequest.bottom;
        setLastBottomKey(requestKey);

        if (direction === 'reverse') {
            // Reverse started: hide bottom immediately
            setBottomVisible(false);
        }
        // Forward: DO NOT show here - wait for handleMugFrameNearEnd callback
    }, [mugRequest.bottom, lastBottomKey]);

    // --- Expose callback handler via ref ---
    useImperativeHandle(ref, () => ({
        handleMugFrameNearEnd: (half: 'top' | 'bottom', direction: 'forward' | 'reverse') => {
            if (direction === 'forward') {
                if (half === 'top') {
                    setTopVisible(true);
                } else {
                    setBottomVisible(true);
                }
            }
        },
        handleCheersNearEnd: () => {
            // Show both halves 1 frame before Cheers animation ends
            setTopVisible(true);
            setBottomVisible(true);
        }
    }), []);

    // If image not loaded or neither half is visible, don't render
    if (!imageLoaded || (!topVisible && !bottomVisible)) return null;

    const overlayStyle = getMugOverlayStyle(isMobileShell);

    return (
        <div
            className="fixed z-30 pointer-events-none"
            style={{
                ...overlayStyle,
                overflow: 'hidden',
            }}
        >
            {/* Top Half */}
            {topVisible && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '50%',
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src={staticMugFrame}
                        alt=""
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '200%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                        }}
                    />
                </div>
            )}

            {/* Bottom Half */}
            {bottomVisible && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '50%',
                        overflow: 'hidden',
                    }}
                >
                    <img
                        src={staticMugFrame}
                        alt=""
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: '200%',
                            objectFit: 'cover',
                            objectPosition: 'bottom',
                        }}
                    />
                </div>
            )}
        </div>
    );
});

export default StaticMug;
